import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, TrendingDown, Users, FileText, Eye, Heart, MessageCircle, ThumbsUp, Share2, DollarSign, LaughIcon, AngryIcon, IndianRupeeIcon } from 'lucide-react';
import { AnalyticsService } from '../../../../../../core/services';
import { Skeleton, Tooltip, styled, tooltipClasses } from '@mui/material';
import { Methods } from '../../../../../../core/utility';
import { InfoOutlined } from '@mui/icons-material';
import { usePdfMode, usePdfVariant } from './pdf/PdfModeContext';
const methods = new Methods()
const analyticsService = new AnalyticsService();
interface MetricsOverviewProps {
  platform: string;
  totalSpent?: number;
  dataMetrics?: any;
  loading?: boolean;
  // Shared (public) links must not expose cost -- hides Total Spent
  shared?: boolean;
}

const mockData = {
  all: {
    influencers: 156,
    posts: 892,
    views: 4567823,
    engagement: 234567,
    engagementRate: 5.14,
    likes: 189432,
    comments: 34567,
    avgViews: 5120,
    shares: 0
  },
  instagram: {
    influencers: 68,
    posts: 412,
    views: 2134567,
    engagement: 123456,
    engagementRate: 5.78,
    likes: 98765,
    comments: 18234,
    avgViews: 5180,
    shares: 0
  },
  facebook: {
    influencers: 52,
    posts: 298,
    views: 1456789,
    engagement: 78934,
    engagementRate: 5.42,
    likes: 56789,
    comments: 12345,
    shares: 1847,
    avgViews: 4889,

  },
  youtube: {
    influencers: 36,
    posts: 182,
    views: 976467,
    engagement: 32177,
    engagementRate: 3.29,
    likes: 33878,
    comments: 3988,
    avgViews: 5365,
    shares: 0
  }
};
// export function MetricsOverview({ platform, totalSpent = 0, dataMetrics, loading }: MetricsOverviewProps) {
export function MetricsOverview({ platform, totalSpent = 0, dataMetrics, loading, shared = false }: MetricsOverviewProps) {
  const pdfMode = usePdfMode();
  const pdfVariant = usePdfVariant();
  // Desktop PDF: the metrics are the whole first page -- 3 big cards per row, sized to fill it
  const bigPdf = pdfVariant === 'desktop';
  const fallback = mockData[platform as keyof typeof mockData] || mockData.all;
  const data: any = ["facebook", "instagram", "youtube", "all"].includes(platform) && dataMetrics
    ? dataMetrics
    : fallback;

  const formatPercent = (value: any) => {
    if (value === null || value === undefined) return "--";
    if (Number(value) === 0) return "--";
    return `${Number(value).toFixed(2)}%`;
  };

  const formatValue = (value: any) => {
    if (value === null || value === undefined) return "--";
    if (Number(value) === 0) return "--";
    return methods.formatNumberExtended(value);
  };
  const allPlatformTooltips: any = {
    "Total Spent": "Sum of all campaign costs for all platforms during the selected period.",
    "CPV": "Cost per view for the campaign, calculated as total campaign cost / total views.",
    "Total Influencers": "Unique influencers who published content during the selected period.",
    "Total Posts": "Number of posts published across all platforms during the selected timeframe.",
    "Total Engagement": "Sum of all interactions such as likes, comments, shares, and saves. Measures how actively users interacted with the content.",
    "Avg. Engagement Rate": "Mean engagement rate across all posts, calculated as (total engagements/followers or subscribers) × 100",
  };

  const platformTooltips: any = {
    "Total Spent": "Sum of all campaign costs for this platform during the selected period.",
    "CPV": "Cost per view on this platform, calculated as total cost / total views.",
    "Total Influencers": "Unique influencers who published content on this platform.",
    "Total Posts": "Number of posts published on this platform in the selected timeframe.",
    "Total Engagement": "Sum of all interactions such as likes, comments, shares and saves. Measures how actively users interacted with the content.",
    "Avg. Engagement Rate": "Mean engagement rate across all posts, calculated as (total engagements/followers or subscribers) × 100",
  };
  const getTooltip = (title: string) =>
    platform === "all"
      ? allPlatformTooltips[title]
      : platformTooltips[title];
  // Base metrics for all platforms
  const totalViewsForCpv = Number(
    platform === "facebook" || platform === "instagram" ? data?.total_play_count : data?.total_views
  ) || 0;
  const campaignCpv = totalViewsForCpv > 0 ? (Number(data?.total_cost) || 0) / totalViewsForCpv : 0;


const ValueWithTooltip = ({ value, formatted }: any) => {
  if (value === null || value === undefined || value === "--") {
    return <span>--</span>;
  }

  // PDF report: exact value printed under the short one (tooltip on the page)
  if (pdfMode && Number(value) >= 100000) {
    return (
      <span>
        {formatted}
        <span style={{ display: 'block', fontSize: bigPdf ? 16 : 12, color: '#64748b', marginTop: bigPdf ? 4 : 2 }}>
          {Number(value).toLocaleString("en-IN")}
        </span>
      </span>
    );
  }

  // If value < 1 lakh → no tooltip
  if (Number(value) < 100000) {
    return <span>{formatted}</span>;
  }

  // Tooltip for >= 1 lakh
  return (
    <Tooltip
      title={Number(value).toLocaleString("en-IN")} 
      placement="top"
      arrow
    >
      <span className="cursor-default">{formatted}</span>
    </Tooltip>
  );
};
const baseMetrics = [
  {
    title: "Total Spent",
    value: (
      <ValueWithTooltip
        value={data?.total_cost}
        formatted={
          data?.total_cost
            ? `₹${formatValue(data.total_cost)}`
            : "--"
        }
      />
    ),
    icon: IndianRupeeIcon,
    // show: true,
    show: !shared,
    tooltip: getTooltip("Total Spent")
  },

  {
    title: "Total Influencers",
    value: (
      <ValueWithTooltip
        value={data?.total_influencers}
        formatted={formatValue(data.total_influencers)}
      />
    ),
    icon: Users,
    show: true,
    tooltip: getTooltip("Total Influencers")
  },

  {
    title: "Total Posts",
    value: (
      <ValueWithTooltip
        value={data?.total_posts}
        formatted={formatValue(data.total_posts)}
      />
    ),
    icon: FileText,
    show: true,
    tooltip: getTooltip("Total Posts")
  },

  {
    title: "Total Views",
    value: (
      <ValueWithTooltip
        value={
          platform === "facebook" || platform === "instagram"
            ? data?.total_play_count
            : platform === "youtube" || platform === "all"
            ? data?.total_views
            : "--"
        }
        formatted={
          platform === "facebook" || platform === "instagram"
            ? formatValue(data.total_play_count)
            : platform === "youtube" || platform === "all"
            ? formatValue(data.total_views)
            : "--"
        }
      />
    ),
    icon: Eye,
    show: true,
    tooltip: getTooltip("Total Views")
  },

  // Campaign-level CPV = total cost / total views (per-post CPV is hidden on shared links)
  {
    title: "CPV",
    value: (
      <span>
        {campaignCpv > 0 ? `₹${campaignCpv.toFixed(2)}` : "--"}
      </span>
    ),
    icon: IndianRupeeIcon,
    show: true,
    tooltip: getTooltip("CPV")
  },

  {
    title: "Total Engagement",
    value: (
      <ValueWithTooltip
        value={
          platform === "facebook"
            ? data.total_like + data.total_comments + data.total_shares
            : platform === "instagram"
            ? data.engagement_rate
            : platform === "youtube"
            ? data.total_like + data.total_comments
            : platform === "all"
            ? data.total_like + data.total_comments
            : "--" 
        }
        formatted={
          platform === "facebook"
            ? formatValue(
                data.total_like +
                  data.total_comments +
                  data.total_shares
              )
            : platform === "instagram"
            ? formatValue(data.engagement_rate)
            : platform === "youtube"
            ? formatValue(data.total_like + data.total_comments)
            : platform === "all"
            ? formatValue(data.total_like + data.total_comments)
            : "--"
        }
      />
    ),
    icon: Heart,
    show: true,
    tooltip: getTooltip("Total Engagement")
  },

  {
    title: "Avg. Engagement Rate",
    value: (
      <ValueWithTooltip
        value={
          platform === "facebook"
            ? data?.engagement_rate
            : platform === "instagram"
            ? data?.avg_er_without_plays
            : platform === "youtube"
            ? data?.avg_engagement_rate
            : platform === "all"
            ? data.engagement_rate
            : "--"
        }
        formatted={
          platform === "facebook"
            ? formatPercent(data.engagement_rate)
            : platform === "instagram"
            ? formatPercent(data.avg_er_without_plays)
            : platform === "youtube"
            ? formatPercent(data.avg_engagement_rate)
            : platform === "all"
            ? formatPercent(data.engagement_rate)
            : "--"
        }
      />
    ),
    icon: TrendingUp,
    show: true,
    tooltip: getTooltip("Avg. Engagement Rate")
  },

  {
    title: "Total Likes",
    value: (
      <ValueWithTooltip
        value={data?.total_like}
        formatted={formatValue(data.total_like)}
      />
    ),
    icon: ThumbsUp,
    show: true,
    tooltip: getTooltip("Total Likes")
  },

  {
    title: "Total Comments",
    value: (
      <ValueWithTooltip
        value={data?.total_comments}
        formatted={formatValue(data.total_comments)}
      />
    ),
    icon: MessageCircle,
    show: true,
    tooltip: getTooltip("Total Comments")
  },

  {
    title: "Total Shares",
    value: (
      <ValueWithTooltip
        value={data?.total_shares}
        formatted={formatValue(data.total_shares)}
      />
    ),
    icon: Share2,
    show: platform === "facebook",
    tooltip: getTooltip("Total Shares")
  }
];


  const supportedPlatforms = ["facebook", "instagram", "youtube", "twitter", "all"];
  const metrics = supportedPlatforms.includes(platform)
    ? baseMetrics.filter(metric => metric.show)
    : [];
  const iconColors = [
    { bg: 'bg-purple-50', icon: 'text-purple-600' },
    { bg: 'bg-blue-50', icon: 'text-blue-600' },
    { bg: 'bg-purple-50', icon: 'text-purple-600' },
    { bg: 'bg-green-50', icon: 'text-green-600' },
    { bg: 'bg-orange-50', icon: 'text-orange-600' },
    { bg: 'bg-pink-50', icon: 'text-pink-600' },
    { bg: 'bg-red-50', icon: 'text-red-600' },
    { bg: 'bg-blue-50', icon: 'text-blue-600' },
    { bg: 'bg-teal-50', icon: 'text-teal-600' }
  ];
  // 🔥 SKELETON LOADING UI
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-10 rounded-lg" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-6 w-20 mb-2" />
              {/* <Skeleton className="h-3 w-32" /> */}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  const DarkTooltip = styled(({ className, ...props }: any) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#2b2b2b",
      color: "#fff",
      fontSize: "0.75rem",
      padding: "8px 10px",
      borderRadius: 6,
      border: "1px solid #2b2b2b",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: "#2b2b2b",
    },
  }));
  // card height that fills what's left of page 1 below the title (~640px of the 1280px-wide report)
  const pdfRows = Math.ceil(metrics.length / 3);
  const pdfCardH = Math.min(200, Math.floor((640 - (pdfRows - 1) * 20) / pdfRows));
  return (

    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${platform === 'facebook' ? ' xl:grid-cols-5' : 'xl:grid-cols-4'}`}
      // PDF: fixed column count (inline style wins over the pinned layout) --
      // two per row on mobile, three big ones on desktop
      style={
        pdfVariant === 'mobile'
          ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }
          : bigPdf
          ? { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 20 }
          : undefined
      }
    >
      {metrics.map((metric, index) => {
        const Icon = metric.icon;
        const colors = iconColors[index % iconColors.length];
        return (
          <Card key={index} style={bigPdf ? { minHeight: pdfCardH, justifyContent: 'center' } : undefined}>
            <CardHeader className={`flex ${bigPdf ? 'px-5' : 'px-3'} flex-row justify-between space-y-0 pb-2`}>
              <div className="flex gap-2 text-slate-600">

                <span style={{ display: 'flex', alignItems: 'center', fontSize: bigPdf ? '20px' : '15px' }}>{metric.title}{metric.tooltip && !pdfMode && (
                  <DarkTooltip style={{ marginLeft: '4px' }} title={metric.tooltip} placement="top" arrow>
                    <button
                      type="button"
                      className="inline-flex p-0 m-0 bg-transparent border-0 
                 cursor-pointer outline-none focus:outline-none"
                    >
                      <InfoOutlined className="h-2 w-2 text-slate-400 hover:text-slate-200" style={{ fontSize: '18px', marginLeft: '5px' }} />
                    </button>
                  </DarkTooltip>
                )}
                </span>

              </div>
              <div className={`${bigPdf ? 'h-11 w-11' : 'h-8 w-8'} rounded-lg ${colors.bg} flex items-center justify-center`}>
                <Icon className={`${bigPdf ? 'h-6 w-6' : 'h-4 w-4'} ${colors.icon}`} />
              </div>
            </CardHeader>
            <CardContent className={bigPdf ? 'px-5' : 'px-3'}>
              <div className="text-slate-900 mb-1" style={bigPdf ? { fontSize: 34, fontWeight: 600, lineHeight: 1.2, minHeight: 64 } : undefined}>{metric.value}</div>
              {/* {metric.change && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  {metric.trend === 'up' ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {metric.change} from last period
                </p>
              )} */}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
